// ---------------------------------------------------------------------------
// Icons — inline SVG icon system (emoji purge, Cut W6).
//
// Two families:
//   - Glyphs: stroke style matching the sidebar's inline SVGs
//     (strokeWidth 1.6, round caps/joins, currentColor, viewBox 24).
//   - Brand marks: filled, simplified provider logos for the integrations
//     catalog. Generic marks (calendar, card, phone, n8n) stay stroke-style.
//
// Every icon renders at 1em by default; size with Tailwind (w-4 h-4 etc.) —
// CSS classes override the width/height presentation attributes.
// ---------------------------------------------------------------------------

import type { FC, ReactNode } from 'react';

export interface IconProps {
  className?: string;
}

function Glyph({ className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function Mark({ className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Glyphs — status + UI
// ─────────────────────────────────────────────────────────────────────────

export const IconCheck: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M5 13l4 4L19 7" />
  </Glyph>
);

export const IconCross: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M6 6l12 12M18 6L6 18" />
  </Glyph>
);

export const IconLock: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <rect x="5" y="11" width="14" height="9" rx="1.5" />
    <path d="M8 11V7a4 4 0 018 0v4" />
  </Glyph>
);

export const IconPin: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M12 21s-7-5.1-7-11a7 7 0 0114 0c0 5.9-7 11-7 11z" />
    <circle cx="12" cy="10" r="2.5" />
  </Glyph>
);

export const IconPrinter: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M6 9V3h12v6" />
    <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
    <rect x="6" y="14" width="12" height="7" rx="1" />
  </Glyph>
);

export const IconBolt: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
  </Glyph>
);

export const IconRepeat: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M17 2l4 4-4 4" />
    <path d="M3 11V9a4 4 0 014-4h14" />
    <path d="M7 22l-4-4 4-4" />
    <path d="M21 13v2a4 4 0 01-4 4H3" />
  </Glyph>
);

export const IconDownload: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <path d="M7 10l5 5 5-5" />
    <path d="M12 15V3" />
  </Glyph>
);

export const IconFolder: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
  </Glyph>
);

export const IconFile: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z" />
    <path d="M13 2v7h7" />
  </Glyph>
);

export const IconFileText: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z" />
    <path d="M13 2v7h7" />
    <path d="M8 13h8M8 17h5" />
  </Glyph>
);

export const IconFileCode: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z" />
    <path d="M13 2v7h7" />
    <path d="M10 13l-2 2.5 2 2.5M14 13l2 2.5-2 2.5" />
  </Glyph>
);

export const IconImage: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="M21 15l-5-5L5 21" />
  </Glyph>
);

/** Generic connector glyph — fallback for unknown provider ids. */
export const IconLink: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
  </Glyph>
);

// ─────────────────────────────────────────────────────────────────────────
// Glyphs — agent kinds
// ─────────────────────────────────────────────────────────────────────────

export const IconAgentRafael: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M12 4c.9 4.9 3.1 7.1 8 8-4.9.9-7.1 3.1-8 8-.9-4.9-3.1-7.1-8-8 4.9-.9 7.1-3.1 8-8z" />
  </Glyph>
);

export const IconAgentRafaels: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
  </Glyph>
);

export const IconAgentScheduler: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </Glyph>
);

export const IconAgentMarketer: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M3 9v6h4l8 5V4L7 9H3z" />
    <path d="M18.5 8.5a5 5 0 010 7" />
  </Glyph>
);

export const IconAgentInventory: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <rect x="3.5" y="13" width="7" height="7" rx="1" />
    <rect x="13.5" y="13" width="7" height="7" rx="1" />
    <rect x="8.5" y="4" width="7" height="7" rx="1" />
  </Glyph>
);

export const IconAgentGrowth: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M3 17l6-6 4 4 8-8" />
    <path d="M15 7h6v6" />
  </Glyph>
);

export const IconAgentAccounting: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M12 3v18" />
    <path d="M5 7h9a3 3 0 010 6H7a3 3 0 000 6h11" />
  </Glyph>
);

export const IconAgentReviews: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M12 4l2.35 4.76 5.25.76-3.8 3.7.9 5.23L12 16.9l-4.7 2.35.9-5.23-3.8-3.7 5.25-.76z" />
  </Glyph>
);

/** Agent-kind glyph map — keyed by agentKind values used across the app. */
export const AGENT_ICONS: Record<string, FC<IconProps>> = {
  rafael: IconAgentRafael,
  rafaels: IconAgentRafaels,
  scheduler: IconAgentScheduler,
  marketer: IconAgentMarketer,
  inventory: IconAgentInventory,
  growth: IconAgentGrowth,
  accounting: IconAgentAccounting,
  reviews: IconAgentReviews,
};

/** Single source of truth for how each agent is named + described in the UI
 * (Home tiles, Agent Fleet, the per-agent workspace). Plain, owner-facing. */
export const AGENT_META: Record<string, { label: string; blurb: string }> = {
  rafael:     { label: 'Chief of staff', blurb: 'Reads the whole business and flags what needs you' },
  rafaels:    { label: 'Front desk',     blurb: 'Answers customers and takes orders on WhatsApp/Telegram' },
  scheduler:  { label: 'Scheduler',      blurb: 'Watches orders and flags anything at risk' },
  marketer:   { label: 'Marketing',      blurb: 'Wins back quiet customers and runs campaigns' },
  inventory:  { label: 'Stock',          blurb: 'Watches stock and suggests what to reorder' },
  growth:     { label: 'Coach',          blurb: 'One focused move to grow, each week' },
  accounting: { label: 'Accountant',     blurb: 'Chases overdue invoices, verifies transfers, watches cash' },
  reviews:    { label: 'Reviews',        blurb: 'Asks happy customers for a Google review' },
};

export function agentLabel(kind: string): string {
  return AGENT_META[kind]?.label ?? 'Assistant';
}

// ─────────────────────────────────────────────────────────────────────────
// Brand marks — filled, simplified provider logos
// ─────────────────────────────────────────────────────────────────────────

export const BrandGoogle: FC<IconProps> = (p) => (
  <Mark {...p}>
    <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
  </Mark>
);

export const BrandMicrosoft: FC<IconProps> = (p) => (
  <Mark {...p}>
    <rect x="3" y="3" width="8.5" height="8.5" />
    <rect x="12.5" y="3" width="8.5" height="8.5" />
    <rect x="3" y="12.5" width="8.5" height="8.5" />
    <rect x="12.5" y="12.5" width="8.5" height="8.5" />
  </Mark>
);

export const BrandStripe: FC<IconProps> = (p) => (
  <Mark {...p}>
    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305z" />
  </Mark>
);

export const BrandXero: FC<IconProps> = (p) => (
  <Mark {...p}>
    <path
      fillRule="evenodd"
      d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 1.8a8.2 8.2 0 110 16.4 8.2 8.2 0 010-16.4z"
    />
    <path
      d="M8.8 8.8l6.4 6.4M15.2 8.8l-6.4 6.4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
    />
  </Mark>
);

export const BrandHubspot: FC<IconProps> = (p) => (
  <Mark {...p}>
    <circle cx="14.5" cy="13.5" r="4.2" fill="none" stroke="currentColor" strokeWidth={2.2} />
    <circle cx="5" cy="4.5" r="1.9" />
    <path d="M6.2 5.9l4.7 4.6" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" />
    <circle cx="7" cy="20.5" r="1.6" />
    <path d="M8 19.4l3.3-3.2" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" />
  </Mark>
);

export const BrandGmail: FC<IconProps> = (p) => (
  <Mark {...p}>
    <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 010 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" />
  </Mark>
);

export const BrandApple: FC<IconProps> = (p) => (
  <Mark {...p}>
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </Mark>
);

export const BrandWhatsApp: FC<IconProps> = (p) => (
  <Mark {...p}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </Mark>
);

export const BrandSlack: FC<IconProps> = (p) => (
  <Mark {...p}>
    <path d="M5.042 15.165a2.528 2.528 0 01-2.52 2.523A2.528 2.528 0 010 15.165a2.527 2.527 0 012.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 012.521-2.52 2.527 2.527 0 012.521 2.52v6.313A2.528 2.528 0 018.834 24a2.528 2.528 0 01-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 01-2.521-2.52A2.528 2.528 0 018.834 0a2.528 2.528 0 012.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 012.521 2.521 2.528 2.528 0 01-2.521 2.521H2.522A2.528 2.528 0 010 8.834a2.528 2.528 0 012.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 012.522-2.521A2.528 2.528 0 0124 8.834a2.528 2.528 0 01-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 01-2.523 2.521 2.527 2.527 0 01-2.52-2.521V2.522A2.527 2.527 0 0115.165 0a2.528 2.528 0 012.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 012.523 2.522A2.528 2.528 0 0115.165 24a2.527 2.527 0 01-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 01-2.52-2.523 2.526 2.526 0 012.52-2.52h6.313A2.527 2.527 0 0124 15.165a2.528 2.528 0 01-2.522 2.523h-6.313z" />
  </Mark>
);

export const BrandTelegram: FC<IconProps> = (p) => (
  <Mark {...p}>
    <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" />
  </Mark>
);

export const BrandN8n: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <circle cx="4.5" cy="12" r="2" />
    <circle cx="12" cy="5.5" r="2" />
    <circle cx="12" cy="18.5" r="2" />
    <circle cx="19.5" cy="12" r="2" />
    <path d="M6.2 10.7l4.4-3.8M6.2 13.3l4.4 3.8M13.8 6.8l4.3 3.7M13.8 17.2l4.3-3.7" />
  </Glyph>
);

export const BrandCalendar: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M8 3v4M16 3v4M3 10h18" />
  </Glyph>
);

export const BrandCard: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <rect x="2.5" y="5.5" width="19" height="13" rx="2" />
    <path d="M2.5 9.5h19M6 15h4" />
  </Glyph>
);

export const BrandPhone: FC<IconProps> = (p) => (
  <Glyph {...p}>
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
  </Glyph>
);

// ─────────────────────────────────────────────────────────────────────────
// ProviderIcon — provider id → brand mark
// ─────────────────────────────────────────────────────────────────────────

const PROVIDER_ICONS: Record<string, FC<IconProps>> = {
  google_workspace: BrandGoogle,
  microsoft_365: BrandMicrosoft,
  stripe_connect: BrandStripe,
  xero: BrandXero,
  hubspot: BrandHubspot,
  google_calendar: BrandCalendar,
  outlook_calendar: BrandCalendar,
  icloud_calendar: BrandApple,
  gmail: BrandGmail,
  whatsapp_business: BrandWhatsApp,
  slack: BrandSlack,
  telegram: BrandTelegram,
  n8n: BrandN8n,
  stripe: BrandCard,
  sms: BrandPhone,
};

export function ProviderIcon({ providerId, className }: { providerId: string; className?: string }) {
  const Icon = PROVIDER_ICONS[providerId] ?? IconLink;
  return <Icon className={className} />;
}
